<template>
  <v-container fluid>
    <v-row>
      <v-col class="px-1" cols="12" md="5">
        <ContainerWithLabel label="Sensor" class="text-caption">
          <template #append-content>
            <v-btn
              v-tooltip:right="
                `Definiert, welcher Sensor für diesen Kanal ausgewertet wird.`
              "
              color="primary"
              density="compact"
              size="small"
              variant="text"
              icon="mdi-help-circle-outline"
            />
          </template>
          <v-select
            v-model="configRef.channels[props.channelNr].sensor.value"
            size="small"
            density="compact"
            hide-details="auto"
            width="225px"
            color="primary"
            :items="
              SWW_SENSORS.slice(0, 3).map((sensor) => ({
                value: sensor.value,
                title: sensor.name,
              }))
            "
          />
        </ContainerWithLabel>
      </v-col>
      <v-col class="px-1" cols="12" md="5">
        <ContainerWithLabel label="Voreinstellungen" class="text-caption">
          <template #append-content>
            <v-btn
              v-tooltip:right="
                `Falls 'Fix' ausgewählt, ist der Kanal eingeschaltet und kann im Betrieb nicht mit dem MT-Sender deaktiviert werden. Falls 'Ein' ausgewählt, ist der Kanal eingeschaltet und die Schwellwerte werden überprüft. Falls 'Aus' ausgewählt, ist der Kanal ausgeschaltet, kann im Betrieb jedoch mit einem MT-Sender eingeschaltet werden`
              "
              density="compact"
              color="primary"
              size="small"
              variant="text"
              icon="mdi-help-circle-outline"
            />
          </template>
          <v-select
            v-model="presetting"
            size="small"
            width="225px"
            color="primary"
            density="compact"
            hide-details="auto"
            :items="[
              {
                value: 0,
                title: 'Aus',
              },
              {
                value: 1,
                title: 'Ein',
              },
              {
                value: 2,
                title: 'Fix',
              },
            ]"
          />
        </ContainerWithLabel>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="px-1" cols="12" md="5">
        <ContainerWithLabel
          class="text-caption"
          :label="`Oberer Schwellwert ${
            SWW_SENSOR_VALUES[configRef.channels[props.channelNr].sensor.value]
              .unit
              ? SWW_SENSOR_VALUES[
                  configRef.channels[props.channelNr].sensor.value
                ].unit
              : ''
          }`"
        >
          <template #append-content>
            <v-btn
              v-tooltip:right="
                'Definiert den Wert, der erreicht werden muss, damit der Kanal aktiviert wird.'
              "
              color="primary"
              density="compact"
              size="small"
              variant="text"
              icon="mdi-help-circle-outline"
            />
          </template>
          <v-select
            v-if="
              SWW_SENSOR_VALUES[
                configRef.channels[props.channelNr].sensor.value
              ].options
            "
            v-model="configRef.channels[props.channelNr].valueUpper.value"
            color="primary"
            density="compact"
            hide-details="auto"
            size="small"
            width="225px"
            :items="
              SWW_SENSOR_VALUES[
                configRef.channels[props.channelNr].sensor.value
              ].options.map((sensorValue) => ({
                value: sensorValue.value,
                title: sensorValue.name,
              }))
            "
          >
          </v-select>
          <v-number-input
            v-else
            v-model="configRef.channels[props.channelNr].valueUpper.value"
            color="primary"
            size="small"
            width="225px"
            density="compact"
            hide-details="auto"
            :min="1"
            :max="15"
          />
        </ContainerWithLabel>
      </v-col>
      <v-col class="px-1" cols="12" md="5">
        <ContainerWithLabel
          class="text-caption"
          :label="`Unterer Schwellwert ${
            SWW_SENSOR_VALUES[configRef.channels[props.channelNr].sensor.value]
              .unit
              ? SWW_SENSOR_VALUES[
                  configRef.channels[props.channelNr].sensor.value
                ].unit
              : ''
          } `"
        >
          <template #append-content>
            <v-btn
              v-tooltip:left="
                'Definiert den Wert, der unterschritten werden muss, damit der Kanal deaktiviert wird.'
              "
              icon="mdi-help-circle-outline"
              color="primary"
              density="compact"
              size="small"
              variant="text"
            />
          </template>
          <!-- Special case: For lower border we want a different name.. -->
          <v-select
            v-if="
              SWW_SENSOR_VALUES[
                configRef.channels[props.channelNr].sensor.value
              ].options
            "
            v-model="configRef.channels[props.channelNr].valueLower.value"
            size="small"
            density="compact"
            width="225px"
            hide-details="auto"
            color="primary"
            :items="
              SWW_SENSOR_VALUES[
                configRef.channels[props.channelNr].sensor.value
              ].options.map((sensorValue) => ({
                value: sensorValue.value,
                title:
                  configRef.channels[props.channelNr].sensor.value === 2 &&
                  sensorValue.value === 120
                    ? 'Kein Regen'
                    : sensorValue.name,
              }))
            "
          />
          <v-number-input
            v-else
            v-model="configRef.channels[props.channelNr].valueLower.value"
            size="small"
            color="primary"
            width="225px"
            density="compact"
            hide-details="auto"
            :min="1"
            :max="15"
          />
        </ContainerWithLabel>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, watch } from 'vue'

import {
  SWW_SENSOR_VALUES,
  SWW_SENSORS,
  SwwConfig,
} from '@/apps/brelag/common/models/swwParams'
import ContainerWithLabel from '@/components/common/ContainerWithLabel.vue'

type SwwChannelProps = {
  channelNr: number
}
const props = defineProps<SwwChannelProps>()

const presetting = ref(0)

const configRef = defineModel<SwwConfig>()

onBeforeMount(() => {
  if (configRef.value.channels[props.channelNr].fix.value) {
    presetting.value = 2
  } else {
    if (configRef.value.channels[props.channelNr].enable.value) {
      presetting.value = 1
    } else {
      presetting.value = 0
    }
  }
})

watch(presetting, () => presettingChanged())

function presettingChanged() {
  if (presetting.value === 0) {
    configRef.value.channels[props.channelNr].fix.value = 0
    configRef.value.channels[props.channelNr].enable.value = 0
  } else if (presetting.value === 1) {
    configRef.value.channels[props.channelNr].fix.value = 0
    configRef.value.channels[props.channelNr].enable.value = 1
  } else {
    configRef.value.channels[props.channelNr].fix.value = 1
    configRef.value.channels[props.channelNr].enable.value = 0
  }
}
</script>

<style></style>
